// @migration-in-progress
/**
 * Familiarisation Helpers
 * Functions to enhance familiarisation with custom company requirements
 * Phase 3: Vessel configuration conditional logic
 */

import { generateFiveSectionFamiliarisation as generateBaseFamiliarisation } from '../config/checklistTemplates/eightSectionFamiliarisation';
import { metaKey } from './metaKey';
import { parseCategory } from './categoryHelpers';

/**
 * Generate familiarisation with custom items included
 * @param {Object} vessel - Current vessel configuration
 * @param {Object} crew - Crew member being familiarised
 * @param {Array} customItems - Custom items from company settings
 * @returns {Object} Familiarisation structure with custom items
 */
export function generateFamiliarisationWithCustomItems(vessel, crew, customItems = []) {
  // Get base familiarisation from regulatory requirements
  const baseFamiliarisation = generateBaseFamiliarisation(vessel, crew);
  
  // Apply vessel configuration filtering
  const filteredFamiliarisation = applyVesselConfigurationFiltering(baseFamiliarisation, vessel);
  
  if (!customItems || customItems.length === 0) {
    return filteredFamiliarisation;
  }

  // Group custom items by section
  const customItemsBySection = {};
  customItems.forEach(item => {
    if (!customItemsBySection[item.section]) {
      customItemsBySection[item.section] = [];
    }
    customItemsBySection[item.section].push({
      ...item,
      isCustom: true,
      // Ensure custom items have proper structure
      id: item.id,
      text: item.text,
      description: item.description || '',
      priority: item.priority || 'normal',
      roles: item.roles || ['all']  // FIX: Preserve roles property
    });
  });

  // Add custom items to appropriate sections
  const enhancedSections = baseFamiliarisation.sections.map(section => {
    const sectionId = section.section;
    const customItemsForSection = customItemsBySection[sectionId] || [];
    
    if (customItemsForSection.length > 0) {
      return {
        ...section,
        items: [
          ...(section.items || []),
          ...customItemsForSection
        ]
      };
    }
    
    return section;
  });

  // Check if we have custom items for company_specific section
  const companySpecificItems = customItemsBySection['company_specific'] || [];
  if (companySpecificItems.length > 0) {
    // Add a new section for company-specific requirements
    enhancedSections.push({
      section: 'company_specific',
      title: 'Company Specific Requirements',
      description: 'Additional requirements as per company SMS',
      reference: 'Company SMS Manual',
      mandatory: true,
      items: companySpecificItems
    });
  }

  // Apply customizations (edited/deleted items) - pass vessel ID for scoped storage
  const customizedFamiliarisation = applyCustomizations({
    ...filteredFamiliarisation,
    sections: enhancedSections
  }, vessel?.id || 'default');

  return customizedFamiliarisation;
}

/**
 * Apply vessel configuration filtering to checklist items
 * Phase 3: Filter out items based on vessel equipment and features
 * @param {Object} familiarisation - Base familiarisation structure
 * @param {Object} vessel - Vessel configuration
 * @returns {Object} Filtered familiarisation
 */
function applyVesselConfigurationFiltering(familiarisation, vessel) {
  if (!vessel || !familiarisation || !familiarisation.sections) {
    return familiarisation;
  }

  // Create vessel config object with standardized property names
  const vesselConfig = {
    // Equipment presence (from VesselSettingsModal)
    liferafts_fitted: vessel.liferafts_fitted || vessel.hasLiferaft || false,
    epirb_required: vessel.hasEPIRB || false,
    sart_fitted: vessel.hasSART || false,
    emergency_steering: vessel.hasEmergencySteering || vessel.emergency_steering || false,
    enclosed_accommodation_present: vessel.enclosed_accommodation_present || vessel.hasAccommodation || false,
    galley_present: vessel.hasGalley || false,
    enclosed_spaces_present: vessel.hasEnclosedSpaces || vessel.enclosed_spaces_present || false,
    
    // Life Saving Appliances
    lifeboat_fitted: vessel.lifeboat_fitted || false,
    rescue_boat_fitted: vessel.rescue_boat_fitted || false,
    liferaft_capacity: parseInt(vessel.liferaft_capacity) || 0,
    
    // Vessel characteristics
    length: parseFloat(vessel.lengthOverall) || 0,
    category: parseCategory(vessel.category),
    vesselType: vessel.type || '',
    
    // Operations
    international_voyages: vessel.internationalVoyages || false,
    oil_capacity_litres: parseFloat(vessel.oilCapacityLitres) || 0,
    operating_hours: vessel.operatingHours || 'under_24h',
    
    // Operations arrays
    operations: vessel.operations || [],
    equipment: vessel.equipment || [],
    extra_equipment: vessel.extra_equipment || [],
    
    // Systems
    power_critical_systems: vessel.power_critical_systems || false,
    towing_capable: vessel.towing_capable || false,
    bilge_auto: vessel.bilge_auto || false,
    GMDSS_equipped: vessel.GMDSS_equipped || false,
    cabin_accommodation: vessel.cabin_accommodation || false,
    
    // Crew setup
    engineer_onboard: vessel.engineer_onboard || false,
    
    // Passenger operations
    passenger_operations: (parseInt(vessel.passengerCapacity) || 0) > 0,
    max_persons: parseInt(vessel.maxPersons) || 0,
    
    // Special equipment checks
    has_winch_lifting_gear: vessel.equipment?.includes('winch_lifting_gear') || false,
    has_gas_detection: vessel.equipment?.includes('gas_detection') || false,
    has_fixed_firefighting: vessel.equipment?.includes('fixed_firefighting') || false,
    has_dangerous_goods: vessel.operations?.includes('dangerous_goods') || false,
    has_diving_support: vessel.operations?.includes('dive_support') || false
  };

  // Filter sections
  const filteredSections = familiarisation.sections.map(section => {
    if (!section.items || section.items.length === 0) {
      return section;
    }

    const originalItemCount = section.items.length;
    
    // Filter items based on trigger conditions
    const filteredItems = section.items.filter(item => {
      // Always include items with no trigger or 'Always' trigger
      if (!item.trigger || item.trigger === 'Always') {
        return true;
      }

      // If trigger is a function, evaluate it with the original vessel object
      // The trigger functions expect the raw vessel object, not our processed vesselConfig
      if (typeof item.trigger === 'function') {
        try {
          return item.trigger(vessel);
        } catch (error) {
          // console.warn('Trigger evaluation failed for item:', item.id, error);
          // Silently fail and include item if trigger evaluation fails
          return true; // Include item if trigger evaluation fails
        }
      }

      return true; // Include by default
    });

    const filteredItemsCount = originalItemCount - filteredItems.length;

    return {
      ...section,
      items: filteredItems,
      filteredItemsCount: filteredItemsCount // Track how many items were filtered out
    };
  });

  // Remove sections that have no items after filtering (except mandatory sections)
  const nonEmptySections = filteredSections.filter(section => 
    section.mandatory || (section.items && section.items.length > 0)
  );

  return {
    ...familiarisation,
    sections: nonEmptySections
  };
}

/**
 * Get display name for custom section
 * @param {string} sectionId - Section identifier
 * @returns {string} Human-readable section name
 */
export function getCustomSectionName(sectionId) {
  const sectionNames = {
    'vessel_operations': 'Vessel & Operations',
    'safety_procedures': 'Safety & Emergency Procedures',
    'equipment_systems': 'Equipment & Systems',
    'regulations_compliance': 'Regulations & Compliance',
    'company_specific': 'Company Specific Requirements'
  };
  
  return sectionNames[sectionId] || sectionId;
}

/**
 * Apply user customizations (edited text, deleted items)
 * @param {Object} familiarisation - Familiarisation structure with sections
 * @param {string} vesselId - Vessel ID for vessel-scoped storage keys
 * @returns {Object} Customized familiarisation
 */
function applyCustomizations(familiarisation, vesselId = 'default') {
  if (!familiarisation || !familiarisation.sections) {
    return familiarisation;
  }

  // Load customizations from vessel-scoped localStorage keys
  const editedItems = JSON.parse(localStorage.getItem(`editedFamiliarisationItems_${vesselId}`) || '{}');
  const deletedItems = JSON.parse(localStorage.getItem(`deletedFamiliarisationItems_${vesselId}`) || '[]');
  
  // Apply customizations to each section
  const customizedSections = familiarisation.sections.map(section => {
    const customizedItems = section.items
      .filter(item => !deletedItems.includes(item.id)) // Remove deleted items
      .map(item => ({
        ...item,
        text: editedItems[item.id] || item.text // Apply edited text
      }));
    
    return {
      ...section,
      items: customizedItems
    };
  });
  
  return {
    ...familiarisation,
    sections: customizedSections
  };
}